import { CookieKeys } from "@outschool/data-schemas";
import {
  EVENT_SOURCE,
  fetchWebsiteExpAssignment,
  HOLDOUT,
} from "@outschool/experiments-shared";
import { useLocalStorageState } from "@outschool/local-storage";
import { Component } from "@outschool/ownership-areas";
import { useTokenContext } from "@outschool/ui-auth";
import { useIsBot } from "@outschool/ui-utils";
import Cookies from "js-cookie";
import isEqual from "lodash/isEqual";
import React, { PropsWithChildren } from "react";
import { v4 } from "uuid";

export const ExperimentsServiceContext = React.createContext<{
  isProduction: boolean;
  isExperimentOverridesEnabled?: boolean;
  eventSource: EVENT_SOURCE;
  loggedInUserExperimentUid: string | undefined;
  loggedOutUserExperimentUid: string | undefined;
  experimentAssignments: Record<string, string>;
  experimentOverrides: Record<string, string>;
  setExperimentOverrides(overrides: Record<string, string>): void;
  shouldTrigger: ({
    experimentName,
    variantAssigned,
    additionalProperties,
  }: {
    experimentName: string;
    variantAssigned: string;
    additionalProperties?: Record<any, any> | undefined;
  }) => boolean;
}>({
  isProduction: false,
  eventSource: EVENT_SOURCE.BROWSER,
  isExperimentOverridesEnabled: false,
  loggedInUserExperimentUid: undefined,
  loggedOutUserExperimentUid: undefined,
  experimentAssignments: {},
  experimentOverrides: {},
  setExperimentOverrides: () => null,
  shouldTrigger: () => true,
});

export function useServerExperimentContext() {
  const context = React.useContext(ExperimentsServiceContext);

  if (context === undefined) {
    throw new Error(
      "useServerExperimentContext called outside of a ExperimentsServiceProvider",
    );
  }

  return context;
}

function getLoggedOutUserExpUid({
  osEnv,
  loggedOutUserExperimentUid,
}: {
  osEnv: string;
  loggedOutUserExperimentUid?: string;
}): string | undefined {
  // server-side
  if (typeof window === "undefined") {
    // ssr-cloudflare-worker has created a loggedOutUserExperimentUid
    if (!!loggedOutUserExperimentUid) {
      Cookies.set(
        CookieKeys.LoggedOutUserExperimentUid,
        loggedOutUserExperimentUid,
        {
          expires: 365, // days
          path: "/",
          sameSite: "lax",
          secure: !(osEnv === "test" || osEnv === "development"),
        },
      );
      return loggedOutUserExperimentUid;
    } else {
      return undefined;
    }
  }

  let uid = Cookies.get(CookieKeys.LoggedOutUserExperimentUid);

  if (!!uid) {
    return uid;
  }

  uid = v4();

  Cookies.set(CookieKeys.LoggedOutUserExperimentUid, uid, {
    expires: 365, // days
    path: "/",
    sameSite: "lax",
    secure: !(osEnv === "test" || osEnv === "development"),
  });

  return uid;
}

/**
 * Returns null if the assignments could not be fetched.
 */
async function fetchExperimentAssignments({
  loggedInUserExpUid,
  loggedOutUserExpUid,
  experimentsServiceUrl,
  isBotRequest,
  isTest,
  timeoutMs,
}: {
  loggedInUserExpUid?: string;
  loggedOutUserExpUid?: string;
  experimentsServiceUrl: string;
  isBotRequest: boolean;
  isTest: boolean;
  timeoutMs?: number;
}) {
  if (
    !isBotRequest &&
    !isTest &&
    (!!loggedOutUserExpUid || !!loggedInUserExpUid)
  ) {
    try {
      const assignmentResponse = await fetchWebsiteExpAssignment({
        experimentsServiceUrl,
        loggedInUserExpUid,
        loggedOutUserExpUid,
        timeoutMs,
      });
      return assignmentResponse;
    } catch (err) {
      OsPlatform.captureError(err, {
        component: Component.ExperimentationFramework,
      });
      return null;
    }
  }
  return {};
}

type TriggerValue = {
  variantAssigned: string;
  additionalProperties?: Record<any, any>;
};

export const ExperimentsServiceProvider: React.FC<
  PropsWithChildren<{
    initialExperimentAssignments?: {
      loggedInUserExpUid: string | undefined;
      loggedOutUserExpUid: string | undefined;
      assignments: Record<string, string>;
    };
    experimentsServiceUrl: string;
    isTest: boolean;
    isProduction: boolean;
    osEnv: string;
    isExperimentOverridesEnabled?: boolean;
    timeoutMs?: number;
    eventSource: EVENT_SOURCE;
    loggedOutUserExperimentUid?: string;
  }>
> = ({
  children,
  initialExperimentAssignments,
  experimentsServiceUrl,
  isTest,
  isProduction,
  osEnv,
  isExperimentOverridesEnabled,
  timeoutMs,
  eventSource,
  loggedOutUserExperimentUid,
}) => {
  const { getTokens } = useTokenContext();
  const loggedInUserExpUid = getTokens()?.decodedToken?.uid;

  const loggedOutUserExpUid = React.useRef(
    getLoggedOutUserExpUid({ osEnv, loggedOutUserExperimentUid }),
  );

  const [experimentOverrides, setExperimentOverrides] = useLocalStorageState(
    "SERVER_SIDE_EXPERIMENT_ASSIGNMENT_OVERRIDES",
    {},
  );
  const [experimentAssignments, setExperimentAssignments] = React.useState(
    initialExperimentAssignments,
  );

  const isBotRequest = useIsBot();

  const experimentTriggers = React.useRef<Record<string, TriggerValue>>({});
  const shouldTrigger = React.useCallback(
    ({
      experimentName,
      variantAssigned,
      additionalProperties,
    }: {
      experimentName: string;
      variantAssigned: string;
      additionalProperties?: Record<any, any>;
    }) => {
      if (variantAssigned === HOLDOUT) {
        return false;
      }
      const triggerValue = {
        variantAssigned,
        additionalProperties,
      };
      if (isEqual(experimentTriggers.current[experimentName], triggerValue)) {
        return false;
      }
      experimentTriggers.current[experimentName] = triggerValue;
      return true;
    },
    [],
  );

  // This will allow us to pass experiment overrides to the server side.
  React.useEffect(() => {
    Cookies.set("experimentOverrides", JSON.stringify(experimentOverrides));
  }, [experimentOverrides]);

  // Callback to refetch experiment assignments. Overwrites the current assignments
  // if the fetch is successful. The intention is that this is only called on user login.
  // Note that experiments need to be refetched from the client side on login because username + password login
  // happens purely on the client side.
  // TODO: In an ideal world, we might prefer to have the logins happen on the server side so that we can refetch experiments from the server side,
  // avoiding a sometimes flaky client-side request, but this is a complex effort due to the features that depend on client-side login to pass state
  const refetchExperimentAssignments = React.useCallback(
    async ({
      loggedInUserExpUid,
      loggedOutUserExpUid,
    }: {
      loggedInUserExpUid?: string;
      loggedOutUserExpUid?: string;
    }) => {
      if (isBotRequest || isTest) {
        setExperimentAssignments({
          loggedInUserExpUid: undefined,
          loggedOutUserExpUid: "",
          assignments: {},
        });
        return;
      }
      const assignments = await fetchExperimentAssignments({
        loggedInUserExpUid,
        loggedOutUserExpUid,
        experimentsServiceUrl,
        isBotRequest,
        isTest,
        timeoutMs,
      });

      // If the assignments request fails, don't overwrite the current assignments.
      // This is because this refetch happens on user login, and logged-out user assignments should still be valid.
      if (assignments !== null) {
        setExperimentAssignments({
          assignments,
          loggedInUserExpUid,
          loggedOutUserExpUid,
        });
      }
    },
    [isBotRequest, isTest, experimentsServiceUrl, timeoutMs],
  );

  /**
   * Refetch experiment assignments if a user logs in.
   * (We reload the page when the user logs out, so no need to refetch there, and the loggedOutUserExpUid doesn't change on the same page.)
   */
  React.useEffect(() => {
    // Only refetch when the user changes.
    if (
      loggedInUserExpUid !== experimentAssignments?.loggedInUserExpUid ||
      loggedOutUserExpUid.current !== experimentAssignments?.loggedOutUserExpUid
    ) {
      refetchExperimentAssignments({
        loggedInUserExpUid,
        loggedOutUserExpUid: loggedOutUserExpUid.current,
      });
    }
    // loggedOutUserExpUid is a ref and should never change, and refetchExperimentAssignments should only change
    // when our fetch parameters change.
  }, [
    loggedInUserExpUid,
    loggedOutUserExpUid,
    refetchExperimentAssignments,
    experimentAssignments?.loggedInUserExpUid,
    experimentAssignments?.loggedOutUserExpUid,
  ]);

  return (
    <ExperimentsServiceContext.Provider
      value={{
        isProduction,
        eventSource,
        isExperimentOverridesEnabled,
        loggedInUserExperimentUid: loggedInUserExpUid,
        loggedOutUserExperimentUid: loggedOutUserExpUid.current,
        experimentAssignments: experimentAssignments?.assignments ?? {},
        experimentOverrides,
        setExperimentOverrides,
        shouldTrigger,
      }}
    >
      {children}
    </ExperimentsServiceContext.Provider>
  );
};
